import React, { useContext, useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { AuthContext, AppSettingsContext } from '../App';
import { useCurrency } from '../contexts/CurrencyContext';
import { useOddsFormat } from '../contexts/OddsFormatContext';
import { Button } from './ui/button';
import NotificationDropdown from './NotificationDropdown';
import { Home, PlusCircle, History, BarChart3, CreditCard, Shield, LogOut, TrendingUp, Lightbulb, BookOpen, ChevronDown, User, DollarSign, Calculator, FileText, ChevronLeft, ChevronRight, Zap } from 'lucide-react';
import axios from 'axios';

const Layout = ({ children }) => {
  const { user, logout } = useContext(AuthContext);
  const appSettings = useContext(AppSettingsContext);
  const { currency, setCurrency, availableCurrencies, currencySymbols } = useCurrency();
  const { oddsFormat, setOddsFormat } = useOddsFormat();
  const navigate = useNavigate();
  const location = useLocation();
  const [openDropdown, setOpenDropdown] = useState(null);
  const [tipsterServiceId, setTipsterServiceId] = useState(null);
  const profileDropdownRef = useRef(null);
  
  // Mobile nav scroll state
  const navScrollRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

  // Fetch tipster's service ID if user is a tipster
  useEffect(() => {
    const fetchTipsterService = async () => {
      if (user?.subscription_plan === 'tipster') {
        try {
          const token = localStorage.getItem('session_token');
          const response = await axios.get(`${API}/tipster-services/my/service`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          if (response.data && response.data.id) {
            setTipsterServiceId(response.data.id);
          }
        } catch (error) {
          console.error('Error fetching tipster service:', error);
        }
      }
    };

    fetchTipsterService();
  }, [user]);

  // Close dropdown on route change
  useEffect(() => {
    setOpenDropdown(null);
  }, [location.pathname]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (profileDropdownRef.current && !profileDropdownRef.current.contains(event.target)) {
        if (openDropdown === 'profile') {
          setOpenDropdown(null);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [openDropdown]);

  // Check if nav can scroll
  const checkNavScroll = () => {
    const nav = navScrollRef.current;
    if (nav) {
      setCanScrollLeft(nav.scrollLeft > 0);
      setCanScrollRight(nav.scrollLeft < nav.scrollWidth - nav.clientWidth - 5);
    }
  };

  useEffect(() => {
    checkNavScroll();
    window.addEventListener('resize', checkNavScroll);
    return () => window.removeEventListener('resize', checkNavScroll);
  }, []);

  const scrollNav = (direction) => {
    const nav = navScrollRef.current;
    if (nav) {
      const scrollAmount = 150;
      nav.scrollBy({ 
        left: direction === 'left' ? -scrollAmount : scrollAmount, 
        behavior: 'smooth' 
      });
      setTimeout(checkNavScroll, 300);
    }
  };

  // Toggle profile dropdown (for mobile click support)
  const toggleProfileDropdown = (e) => {
    e.stopPropagation();
    setOpenDropdown(prev => prev === 'profile' ? null : 'profile');
  };

  // Main navigation structure with submenu support
  const navItems = [
    { path: '/dashboard', label: 'Dashboard', icon: Home },
    { path: '/bets/add', label: 'Add Bet', icon: PlusCircle },
    { path: '/reports', label: 'Reports', icon: BarChart3 },
    { 
      path: '/tip-services', 
      label: 'Tip Services', 
      icon: Lightbulb,
      submenu: user?.subscription_plan === 'tipster' ? [
        { path: '/tip-services', label: 'Tip Services', icon: Lightbulb },
        { path: '/predictions-ai', label: 'AI Predictions', icon: TrendingUp },
        { path: '/stats', label: 'Stats Query', icon: Zap, premium: true },
        { path: '/add-service', label: 'My Service', icon: Shield },
        ...(tipsterServiceId ? [{ path: `/tip-services/${tipsterServiceId}`, label: 'My Tips', icon: FileText }] : [])
      ] : [
        { path: '/tip-services', label: 'Tip Services', icon: Lightbulb },
        { path: '/predictions-ai', label: 'AI Predictions', icon: TrendingUp },
        { path: '/stats', label: 'Stats Query', icon: Zap, premium: true }
      ]
    },
    { path: '/betting-strategies', label: 'Betting Strategies', icon: BookOpen },
    { path: '/subscription', label: 'Subscription', icon: CreditCard },
  ];
  
  // Mobile-specific items (no submenus)
  const mobileNavItems = [
    { path: '/dashboard', label: 'Dashboard', icon: Home },
    { path: '/bets/add', label: 'Add Bet', icon: PlusCircle },
    { path: '/reports', label: 'Reports', icon: BarChart3 },
    { path: '/tip-services', label: 'Tip Services', icon: Lightbulb },
    { path: '/predictions-ai', label: 'AI Predictions', icon: TrendingUp },
    { path: '/stats', label: 'Stats Query', icon: Zap, premium: true },
    ...(user?.subscription_plan === 'tipster' ? [
      { path: '/add-service', label: 'My Service', icon: Shield },
      ...(tipsterServiceId ? [{ path: `/tip-services/${tipsterServiceId}`, label: 'My Tips', icon: FileText }] : [])
    ] : []),
    { path: '/betting-strategies', label: 'Betting Strategies', icon: BookOpen },
    { path: '/subscription', label: 'Subscription', icon: CreditCard },
  ];

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const isPathActive = (item) => {
    if (item.submenu && item.submenu.length > 0) {
      return location.pathname === item.path || item.submenu.some(sub => location.pathname === sub.path);
    }
    return location.pathname === item.path;
  };

  return (
    <div className="min-h-screen">
      {/* Navigation */}
      <nav className="fixed top-0 w-full z-50 glass-dark border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-2 cursor-pointer" onClick={() => navigate('/dashboard')}>
              {appSettings.app_logo_url ? (
                <img 
                  src={appSettings.app_logo_url} 
                  alt={appSettings.app_name} 
                  className="h-10 max-w-[200px] object-contain"
                />
              ) : (
                <>
                  <TrendingUp className="w-8 h-8 text-blue-400" />
                  <span className="text-lg sm:text-xl font-bold text-white whitespace-nowrap">{appSettings.app_name}</span>
                </>
              )}
            </div>

            <div className="hidden xl:flex items-center space-x-1">
              {navItems.map((item) => {
                const Icon = item.icon;
                const isActive = isPathActive(item);
                const hasSubmenu = item.submenu && item.submenu.length > 0;
                
                return (
                  <div 
                    key={item.path} 
                    className="relative"
                    onMouseEnter={() => hasSubmenu && setOpenDropdown(item.path)}
                    onMouseLeave={() => hasSubmenu && setOpenDropdown(null)}
                  >
                    <Button
                      data-testid={`nav-${item.label.toLowerCase().replace(/ /g, '-')}`}
                      onClick={() => {
                        navigate(item.path);
                        setOpenDropdown(null);
                      }}
                      variant="ghost"
                      size="sm"
                      className={`text-white hover:bg-white/10 text-sm px-2 ${
                        isActive ? 'bg-white/10' : ''
                      }`}
                    >
                      <Icon className="w-4 h-4 mr-1" />
                      <span className="hidden 2xl:inline">{item.label}</span>
                      <span className="inline 2xl:hidden">{item.label.split(' ')[0]}</span>
                      {hasSubmenu && <ChevronDown className="w-3 h-3 ml-1" />}
                    </Button>
                    
                    {/* Submenu Dropdown */}
                    {hasSubmenu && openDropdown === item.path && (
                      <div className="absolute top-full left-0 mt-0 w-48 bg-slate-900/95 backdrop-blur-lg border border-white/10 rounded-md shadow-lg z-50">
                        <div className="py-1">
                          {item.submenu.map((subItem) => {
                            const SubIcon = subItem.icon;
                            return (
                              <button
                                key={subItem.path}
                                onClick={() => {
                                  navigate(subItem.path);
                                  setOpenDropdown(null);
                                }}
                                className={`w-full text-left px-4 py-2 text-sm text-white hover:bg-white/10 flex items-center ${
                                  location.pathname === subItem.path ? 'bg-white/10' : ''
                                }`}
                              >
                                <SubIcon className="w-4 h-4 mr-2" />
                                <span className="flex-1">{subItem.label}</span>
                                {subItem.premium && (
                                  <span className="ml-2 text-[10px] font-bold bg-amber-500/20 text-amber-300 border border-amber-500/30 px-1.5 py-0.5 rounded">PRO</span>
                                )}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            <div className="flex items-center space-x-4">
              {/* Notification Bell */}
              <NotificationDropdown onOpen={() => setOpenDropdown(null)} />

              {/* Profile Dropdown */}
              <div 
                ref={profileDropdownRef}
                className="relative"
                onMouseEnter={() => window.innerWidth >= 1024 && setOpenDropdown('profile')}
                onMouseLeave={() => window.innerWidth >= 1024 && setOpenDropdown(null)}
              >
                <Button
                  onClick={toggleProfileDropdown}
                  variant="ghost"
                  className="text-white hover:bg-white/10"
                >
                  <div className="hidden sm:block text-right mr-2">
                    <p className="text-white font-medium text-sm">{user?.name}</p>
                    <p className="text-xs text-gray-400 capitalize">{user?.subscription_plan} Plan</p>
                  </div>
                  <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-500 rounded-full flex items-center justify-center">
                    <span className="text-white font-bold">{user?.name?.charAt(0).toUpperCase()}</span>
                  </div>
                </Button>
                
                {/* Profile Dropdown Menu */}
                {openDropdown === 'profile' && (
                  <div className="absolute top-full right-0 mt-0 w-48 bg-slate-900/95 backdrop-blur-lg border border-white/10 rounded-md shadow-lg z-50">
                    <div className="py-1">
                      <div className="px-4 py-2 border-b border-white/10 sm:hidden">
                        <p className="text-white font-medium text-sm">{user?.name}</p>
                        <p className="text-xs text-gray-400 capitalize">{user?.subscription_plan}</p>
                      </div>
                      <button
                        onClick={() => {
                          navigate('/profile');
                          setOpenDropdown(null);
                        }}
                        className="w-full text-left px-4 py-2 text-sm text-white hover:bg-white/10 flex items-center"
                      >
                        <User className="w-4 h-4 mr-2" />
                        My Profile
                      </button>
                      <button
                        onClick={() => {
                          navigate('/user-guide');
                          setOpenDropdown(null);
                        }}
                        className="w-full text-left px-4 py-2 text-sm text-white hover:bg-white/10 flex items-center"
                      >
                        <BookOpen className="w-4 h-4 mr-2" />
                        User Guide
                      </button>
                      <button
                        onClick={() => {
                          navigate('/blog');
                          setOpenDropdown(null);
                        }}
                        className="w-full text-left px-4 py-2 text-sm text-white hover:bg-white/10 flex items-center"
                      >
                        <FileText className="w-4 h-4 mr-2" />
                        Blog
                      </button>
                      {user?.is_admin && (
                        <button
                          onClick={() => {
                            navigate('/admin');
                            setOpenDropdown(null);
                          }}
                          className="w-full text-left px-4 py-2 text-sm text-white hover:bg-white/10 flex items-center"
                        >
                          <Shield className="w-4 h-4 mr-2" />
                          Admin Dashboard
                        </button>
                      )}
                      
                      {/* Currency Selector */}
                      <div className="border-t border-white/10 px-4 py-2">
                        <div className="flex items-center text-gray-400 text-xs mb-1">
                          <DollarSign className="w-3 h-3 mr-1" />
                          Currency
                        </div>
                        <select
                          value={currency}
                          onChange={(e) => setCurrency(e.target.value)}
                          className="w-full bg-white/5 border border-white/10 text-white text-sm rounded px-2 py-1.5 focus:outline-none focus:border-blue-500"
                        >
                          {availableCurrencies.map((curr) => (
                            <option key={curr} value={curr} className="bg-slate-900">
                              {currencySymbols[curr]} {curr}
                            </option>
                          ))}
                        </select>
                      </div>

                      {/* Odds Format Selector */}
                      <div className="px-4 py-2">
                        <div className="flex items-center text-gray-400 text-xs mb-1">
                          <Calculator className="w-3 h-3 mr-1" />
                          Odds Format
                        </div>
                        <select
                          value={oddsFormat}
                          onChange={(e) => setOddsFormat(e.target.value)}
                          className="w-full bg-white/5 border border-white/10 text-white text-sm rounded px-2 py-1.5 focus:outline-none focus:border-blue-500"
                        >
                          <option value="decimal" className="bg-slate-900">Decimal (2.50)</option>
                          <option value="fractional" className="bg-slate-900">Fractional (3/2)</option>
                          <option value="american" className="bg-slate-900">American (+150)</option>
                        </select>
                      </div>
                      
                      <button
                        onClick={() => {
                          handleLogout();
                          setOpenDropdown(null);
                        }}
                        className="w-full text-left px-4 py-2 text-sm text-white hover:bg-white/10 flex items-center border-t border-white/10"
                      >
                        <LogOut className="w-4 h-4 mr-2" />
                        Logout
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Mobile & Tablet Navigation (shows on screens smaller than xl/1280px) */}
          <div className="xl:hidden pb-4 relative">
            {/* Left scroll indicator */}
            {canScrollLeft && (
              <button
                onClick={() => scrollNav('left')}
                className="absolute left-0 top-1/2 -translate-y-1/2 z-10 p-1"
              >
                <ChevronLeft className="w-5 h-5 text-blue-400" />
              </button>
            )}
            
            {/* Right scroll indicator */}
            {canScrollRight && (
              <button
                onClick={() => scrollNav('right')}
                className="absolute right-0 top-1/2 -translate-y-1/2 z-10 p-1"
              >
                <ChevronRight className="w-5 h-5 text-blue-400" />
              </button>
            )}

            <div 
              ref={navScrollRef}
              onScroll={checkNavScroll}
              className="overflow-x-auto scrollbar-hide"
            >
              <div className="flex space-x-2 min-w-max px-1">
                {mobileNavItems.map((item) => {
                  const Icon = item.icon;
                  const isActive = location.pathname === item.path;
                  
                  return (
                    <Button
                      key={item.path}
                      onClick={() => navigate(item.path)}
                      variant="ghost"
                      size="sm"
                      className={`text-white hover:bg-white/10 whitespace-nowrap flex-shrink-0 ${
                        isActive ? 'bg-white/10' : ''
                      }`}
                    >
                      <Icon className="w-4 h-4 mr-2" />
                      {item.label}
                    </Button>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </nav>

      {/* Content */}
      <div className="pt-28 xl:pt-16">{children}</div>
    </div>
  );
};

export default Layout;
